/**
 * Constants for React Query keys and configuration
 * All query keys should be defined here to ensure consistent caching behavior
 */

// Student Management Query Keys
export const QUERY_KEYS = {
  // Student related keys
  STUDENTS: 'students',
  STUDENT_DETAILS: 'student-details',
  STUDENT_FORM_METADATA: 'studentFormMetadata',
  STUDENT_ATTENDANCE: 'student-attendance',
  STUDENT_ATTENDANCE_PAGINATED: 'student-attendance-paginated',
  STUDENT_FEES: 'student-fees',
  STUDENT_TRANSPORT: 'student-transport',
  STUDENT_DISCIPLINE: 'student-discipline',
  STUDENT_DOCUMENTS: 'student-documents',
  STUDENT_LOGS: 'student-logs',
  STUDENT_LEAVES: 'student-leaves',
  TC_PREVIEW: 'tc-preview',

  // School related keys
  GRADES: 'grades',
  SECTIONS: 'sections',
  TEACHERS: 'teachers',
  CLASS_METADATA: 'classMetadata',
  ACADEMIC_YEAR: 'academic-year-data',
  ACADEMIC_SESSIONS: 'academic-sessions',
  SYSTEM_CLASS_METADATA: 'system-class-metadata',

  // Faculty related keys
  FACULTY: 'faculty',
  FACULTIES: 'faculties',
  FACULTY_FORM_METADATA: 'facultyFormMetadata',
  FACULTY_BIOMETRIC_LOGS: 'faculty-biometric-logs',
  FACULTY_ATTENDANCE: 'faculty-attendance',
  FACULTY_ATTENDANCE_FOR_SHIFT: 'faculty-attendance-for-shift',
  FACULTY_ASSIGNMENT_OPTIONS: 'faculty-assignment-options',
  FACULTY_ASSIGNMENTS: 'faculty-assignments',
  FACULTY_DOCUMENTS: 'faculty-documents',
  FACULTY_TRANSPORT: 'faculty-transport',

  // Transportation related keys
  VEHICLES: 'vehicles',
  VEHICLE_DETAILS: 'vehicle-details',
  TRANSPORTATION_STAFF: 'transportation-staff',
  TRANSPORTATION_STAFF_ATTENDANCE: 'transportation-staff-attendance',
  VEHICLE_ASSIGNMENTS: 'vehicle-assignments',
  VEHICLE_TEAMS_ASSIGNMENT: 'vehicle-teams-assignment',
  VEHICLE_ASSIGNED_TEAMS: 'vehicle-assigned-teams',

  // Fees related keys
  FEE_METADATA: 'fee-metadata',
  FEE_TEMPLATES: 'fee-templates',
  FEE_TYPES: 'fee-types',
  FEE_DISCOUNTS: 'fee-discounts',
  FEE_TAXES: 'fee-taxes',
  FEES: 'fees',
  PAYMENT_PROOFS: 'payment-proofs',
  PAYMENT_PROOF_GRADES: 'payment-proof-grades',
  PAYMENT_PROOF_SECTIONS: 'payment-proof-sections',
  RECEIPTS: 'receipts',
  RECEIPT_DETAILS: 'receipt-details',
  FEE_REPORTS: 'fee-reports',

  // Settings related keys
  SETTINGS: 'settings',
  ROLES: 'roles',
  PROGRAMMES: 'programmes',
  PERMISSIONS: 'permissions',
  DESIGNATIONS: 'designations',
  BIOMETRIC_DEVICES: 'biometric-devices',
  SHIFTS: 'shifts',

  // Faculty Biometric Configuration keys
  FACULTY_SHIFTS: 'faculty-shifts',
  FACULTY_DEVICE_ASSOCIATIONS: 'faculty-device-associations',

  // Staff related keys
  SUPPORTING_STAFF: 'supporting-staff',
  SUPPORTING_STAFF_ATTENDANCE: 'supporting-staff-attendance',
  TEACHER_ATTENDANCE: 'teacher-attendance',
  HOLIDAY_VALIDATION: 'holiday-validation',
  TEAMS: 'teams',

  // Dashboard related keys
  DASHBOARD: 'dashboard',
  DASHBOARD_STATS: 'dashboard-stats',
  DASHBOARD_RECENT_ACTIVITIES: 'dashboard-recent-activities',
  DASHBOARD_PENDING_ACTIONS: 'dashboard-pending-actions',

  // Notice related keys
  NOTICES: 'notices',
  NOTICE_DETAILS: 'notice-details',
  NOTICE_GRADES: 'notice-grades',
  NOTICE_RECIPIENTS: 'notice-recipients',
  NOTICE_EMAIL_STATUS: 'notice-email-status',

  // Suggestions and Complaints related keys
  SUGGESTIONS_COMPLAINTS: 'suggestions-complaints',

  // Common keys
  USER_PROFILE: 'user-profile',
  NAVBAR_PROFILE: 'navbar-profile',

  ATTENDANCE: {
    STUDENT: (studentId: string) => ['attendance', 'student', studentId],
    TEACHER: (teacherId: string) => ['attendance', 'teacher', teacherId],
    TRANSPORTATION: (staffId: string) => [
      'attendance',
      'transportation',
      staffId,
    ],
    SUPPORTING: (staffId: string) => ['attendance', 'supporting', staffId],
    CLASS: (classId: string) => ['attendance', 'class', classId],
    EVENTS: (instituteId: string) => ['attendance', 'events', instituteId],
    HOLIDAYS: (instituteId: string) => ['attendance', 'holidays', instituteId],
  },

  CALENDAR: 'calendar',
  
  // Contact Form related keys
  CONTACT_FORM_SUBMISSION: 'contact-form-submission',
  ASSESSMENT_RESULTS: 'assessment-results',
  ASSESSMENT_RESULTS_FOR_DISPLAY: 'assessment-results-for-display',
  ASSESSMENT_RESULTS_BULK: 'assessment-results-bulk',
  ASSESSMENT_RESULTS_ALL: 'assessment-results-all',
  ASSESSMENTS_FOR_MAPPING: 'assessments-for-mapping',
  ASSESSMENTS: 'assessments',
  STUDENTS_BULK: 'students-bulk',
 
  ALL_STUDENTS: 'all-students',
  ASSESSMENTS_FOR_EXAM_TYPES: 'assessments-for-exam-types',
  ASSESSMENT_CONFIG: 'assessment-config',

  // Admission related keys
  ADMISSION_INQUIRIES: 'admission-inquiries',
  // Reports related keys
  EXPORT_GRADES: 'export-grades',
  EXPORT_HIERARCHICAL_METADATA: 'export-hierarchical-metadata',
} as const;

// Default query configurations for various query types
export const QUERY_CONFIG = {
  // Default config for most queries
  DEFAULT: {
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
    retry: 1,
  },

  // For data that changes frequently (real-time-ish)
  FREQUENT: {
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    staleTime: 30 * 1000, // 30 seconds
    cacheTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
  },

  // For data that rarely changes
  STATIC: {
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    staleTime: 24 * 60 * 60 * 1000, // 24 hours
    cacheTime: 7 * 24 * 60 * 60 * 1000, // 7 days
    retry: 1,
  },

  // For search/filter queries that shouldn't be cached long
  SEARCH: {
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    staleTime: 0, // Always stale
    cacheTime: 5 * 60 * 1000, // 5 minutes
    retry: 0,
  },

  ADMISSION_INQUIRY_DETAIL: {
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    staleTime: 0,
    cacheTime: 10 * 60 * 1000,
    retry: 1,
  },
};

// Cache invalidation events that may trigger refetches across the application
export const INVALIDATION_EVENTS = {
  STUDENT_CREATED: 'student-created',
  STUDENT_UPDATED: 'student-updated',
  STUDENT_DELETED: 'student-deleted',
  GRADE_UPDATED: 'grade-updated',
  SECTION_UPDATED: 'section-updated',
  FACULTY_CREATED: 'faculty-created',
  FACULTY_UPDATED: 'faculty-updated',
  FACULTY_DELETED: 'faculty-deleted',
  VEHICLE_CREATED: 'vehicle-created',
  VEHICLE_UPDATED: 'vehicle-updated',
  VEHICLE_DELETED: 'vehicle-deleted',
  FEE_PAYMENT_RECORDED: 'fee-payment-recorded',
  RECEIPT_GENERATED: 'receipt-generated',
};
